import Joi from 'joi';

export const defaultMemberState = {
  firstNm: '',
  lastNm: '',
  dob: '',
  genderCd: '',
  languageCd: '',
  contactEmail: '',
  effectiveStartDt: '',
  homeAddress: {
    addressLine1: '',
    city: '',
    postal: ''
  },
  phone1: {
    phoneNumber: '',
    phoneFaxTypeCd: ''
  },
  readOnly: false,
  phoneIsPreferred: 'phone1',
  primaryFlg: 'Y',
  relatedInstances: [],
  isMailingAddressDifferent: false
};

const optionalString: () => Joi.StringSchema = () => Joi.string().optional().allow('', null);

const phoneValidation: () => Joi.ObjectSchema = () =>
  Joi.object({
    areaCode: optionalString(),
    phoneFaxTypeCd: optionalString(),
    phoneNumber: optionalString()
      .length(10)
      .pattern(/^[0-9]+$/),
    extension: optionalString(),
    geographicCountryCd: Joi.string().optional().default('USA'),
    preferredFlg: Joi.string().optional().default('N'),
    isPreferred: Joi.boolean().optional().default(false)
  });

export const validationSchema = Joi.object({
  couldBeReactivated: Joi.boolean(),
  clientId: Joi.when('group.consultReimbursementMethodCode', {
    is: 'CONSULTREIMBURSEMENT_CLAIM',
    then: Joi.string().required(),
    otherwise: optionalString()
  }),
  firstNm: Joi.string().required().label('First name'),
  middleNm: optionalString(),
  lastNm: Joi.string()
    .required()
    .label('Last name')
    .pattern(/^[a-zA-Z' -]+$/)
    .messages({
      'string.pattern.base': "Approved special characters include - and '"
    }),
  dob: Joi.date().required().label('Date of Birth').max(new Date()).message('Date must be in past'),
  genderCd: Joi.string().required().label('Gender'),
  languageCd: Joi.string().required().label('Language'),
  group: Joi.object({
    consultReimbursementMethodCode: Joi.string().optional(),
    dependentSourceRegistration: Joi.string().optional(),
    name: Joi.string().optional(),
    primarySourceRegistration: Joi.string().optional(),
    id: Joi.alternatives(Joi.string(), Joi.array().items(Joi.string())),
    label: Joi.string().optional().allow(''),
    value: Joi.string().optional().allow(null)
  }).label('Group'),
  groupId: Joi.number().optional(),
  contactEmail: Joi.string()
    .optional()
    .label('Email')
    .allow('')
    .pattern(/^[^\s@]+@[^\s@]+\.[^\s@]+$/)
    .messages({
      'string.pattern.base': 'Invalid email'
    }),
  createdAt: Joi.string().optional(),
  effectiveStartDt: Joi.date().required().label('Start Date'),
  effectiveEndDt: optionalString(),
  primaryFlg: Joi.string(),
  isMailingAddressDifferent: Joi.boolean().optional().default(false),
  groupSearchTerm: optionalString(),
  homeAddress: Joi.object({
    addressLine1: Joi.string().required().label('Address Line 1'),
    addressLine2: optionalString(),
    addressLine3: optionalString(),
    addressTypeCd: Joi.string().optional().default('ADDRESSTYPE_HOME'),
    city: Joi.string().required().label('City'),
    countryCd: Joi.string().optional().default('USA'),
    isPreferred: Joi.boolean().optional().default(false),
    postal: Joi.string()
      .length(5)
      .pattern(/^[0-9]+$/)
      .required()
      .label('Zip/Postal Code'),
    preferredFlg: Joi.string().optional().default('N'),
    stateProvince: Joi.string().required().label('State/Province')
  }),
  mailingAddress: Joi.object({
    addressLine1: optionalString(),
    addressLine2: optionalString(),
    addressLine3: optionalString(),
    addressTypeCd: Joi.string().optional().default('ADDRESSTYPE_MAIL'),
    city: optionalString(),
    countryCd: Joi.string().optional().default('USA'),
    isPreferred: Joi.boolean().optional().default(false),
    postal: optionalString()
      .length(5)
      .pattern(/^[0-9]+$/),
    preferredFlg: Joi.string().optional().default('N'),
    stateProvince: optionalString()
  }).optional(),
  readOnly: Joi.boolean(),
  relationshipSubtypeCd: Joi.when('primaryFlg', { is: 'N', then: Joi.required() }),
  relatedInstances: Joi.array().items(Joi.object()).optional().default([]),
  relatedInstancesPresent: Joi.boolean(),
  phoneIsPreferred: Joi.string().default('phone1'),
  phone1: Joi.object({
    areaCode: optionalString(),
    phoneFaxTypeCd: Joi.string().required().label('Phone type'),
    phoneNumber: Joi.string()
      .length(10)
      .pattern(/^[0-9]+$/)
      .required()
      .label('Phone number'),
    extension: optionalString(),
    geographicCountryCd: Joi.string().optional().default('USA'),
    preferredFlg: Joi.string().optional().default('N'),
    isPreferred: Joi.boolean().optional().default(false)
  }),
  phone2: phoneValidation().optional(),
  originMember: Joi.object().unknown(true)
});
